import { BannerType } from '@/components/persistent-header-banner'
import { useAuth } from '@/contexts/auth.context'
import { useAuthQuery } from '@/hooks/use-auth-query'
import { useMemo } from 'react'
import { plaidApi } from '../../api'

const usePlaidItems = () => {
  const { user } = useAuth()

  const isFirstLoginModalDismissed = user.isFirstLoginModalDismissed()

  const {
    data: plaidItems,
    isLoading,
    isError,
  } = useAuthQuery({
    queryKey: ['plaid-items'],
    queryFn: plaidApi.getPlaidItems,
  })

  const hasBankAccount = useMemo(() => {
    if (!plaidItems || !Array.isArray(plaidItems)) {
      return false
    }

    return plaidItems.some(item => item.plaidAccount && Array.isArray(item.plaidAccount))
  }, [plaidItems])

  const hasMonitorableBankAccount = useMemo(() => {
    if (!plaidItems || !Array.isArray(plaidItems)) {
      return false
    }

    return plaidItems.some(
      item =>
        item.plaidAccount &&
        Array.isArray(item.plaidAccount) &&
        item.isDefault &&
        item.plaidAccount.some(account => account.monitorable)
    )
  }, [plaidItems])

  // Check if any items need reconnection (for update modal)
  const needsReconnection = useMemo(() => {
    if (!plaidItems || !Array.isArray(plaidItems)) {
      return false
    }

    return plaidItems.some(item => item.itemStatus === 'PENDING_DISCONNECT' || item.itemStatus === 'DISCONNECTED')
  }, [plaidItems])

  // Banner should show if:
  // 1. Data is loaded and not in error state
  // 2. User does not have monitored account
  const showPlaidBannerType = useMemo(() => {
    if (!needsReconnection && !isLoading && !isError && !hasBankAccount) {
      // Prioritize this banner type over the monitorable banner type
      return BannerType.PLAID_NO_LINKED_BANK_ACCOUNT
    }
    if (!needsReconnection && !isLoading && !isError && !hasMonitorableBankAccount) {
      return BannerType.PLAID_NO_MONITORABLE_BANK_ACCOUNT
    }
    return BannerType.NONE
  }, [needsReconnection, isLoading, isError, hasBankAccount, hasMonitorableBankAccount])

  // Update modal should show if:
  // 1. Data is loaded and not in error state
  // 2. User has plaid item that needs reconnection
  const shouldShowUpdateModal = useMemo(() => {
    return !isLoading && !isError && needsReconnection
  }, [isLoading, isError, needsReconnection])

  return {
    plaidItems,
    isLoading,
    isError,
    needsReconnection,
    showPlaidBannerType,
    shouldShowUpdateModal,
    isFirstLoginModalDismissed,
  }
}

export default usePlaidItems
